/**
 * O seletor de ambiente do wizard (consoleai#25804, teste T0 do Felipe em
 * 27/10/2026): DEV, QA, HML ou PRD — e, com o ambiente, o tenant e a conta.
 *
 * As opções vêm do servidor (`GET .../ambientes`, `ambientes_do_rascunho`):
 * a tela não sabe em que tenant mora cada ambiente nem quais existem. Trocar
 * pede confirmação, porque muda a conta; o ambiente sem conta (ou sem acesso)
 * não troca e mostra o caminho que o servidor escreveu.
 */
import { useState } from 'react'
import { Alert, Button, Group, Loader, SegmentedControl, Stack, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { lerAmbientesDoRascunho } from '@/api/rascunhoDeAmbiente'
import { CHAVE_PEDIDO_TERRAFORM } from '@/hooks/useTerraformChangeRequest'
import { Dado } from '../components/Dado'
import { estadoDaQuery, LeituraDoDevops } from './LeituraDoDevops'
import type { PassoProps } from './Passos'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

export function EscolhaDoAmbiente({ r, tenantId, rascunhoId }: PassoProps) {
  const x = r.rascunho
  const q = useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'formulario-ambientes', tenantId, rascunhoId, x?.versao],
    queryFn: () => lerAmbientesDoRascunho(tenantId, rascunhoId),
    enabled: Boolean(x),
    retry: false,
  })
  const [escolhido, setEscolhido] = useState<string | null>(null)
  const [trocando, setTrocando] = useState(false)
  const [recusa, setRecusa] = useState<string | null>(null)
  if (!x) return null
  const atual = x.ambiente.toUpperCase()
  const leitura = estadoDaQuery(q)
  if (leitura !== 'ok' || !q.data) {
    return leitura === 'carregando' ? (
      <Group gap={6}><Loader size={14} aria-hidden /><Text size="sm" style={BARLOW}>Lendo os ambientes do projeto…</Text></Group>
    ) : (
      <LeituraDoDevops estado={leitura} motivo="os ambientes do projeto não carregaram"
                       onTentarDeNovo={() => { void q.refetch() }} />
    )
  }
  const itens = q.data.items
  const alvo = escolhido && escolhido !== atual ? itens.find((i) => i.ambiente === escolhido) : undefined

  const trocar = async () => {
    if (!alvo) return
    setTrocando(true)
    const motivo = await r.trocarAmbiente(alvo.ambiente)
    setTrocando(false)
    setRecusa(motivo)
    if (motivo === null) setEscolhido(null)
  }

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500} style={BARLOW} id="rotulo-do-ambiente">Ambiente</Text>
      <SegmentedControl aria-labelledby="rotulo-do-ambiente"
                        data={itens.map((i) => i.ambiente)}
                        value={escolhido ?? atual}
                        onChange={(v) => { setEscolhido(v); setRecusa(null) }} />
      <Text size="xs" c="dimmed" style={BARLOW}>
        O ambiente decide o tenant e a conta AWS: cada ambiente do projeto tem a sua conta.
      </Text>
      {alvo && alvo.estado === 'disponivel' && alvo.tenant && (
        <Stack gap={6} role="region" aria-label={`Trocar para ${alvo.ambiente}`}>
          <Text size="sm" style={BARLOW}>
            O rascunho passa para o tenant <Dado>{alvo.tenant.nome}</Dado>
            {alvo.account_id ? <>, conta <Dado>{alvo.account_id}</Dado></> : null}. A conta, a role e
            os hosts propostos passam a ser os do {alvo.ambiente}, e a prontidão é conferida de novo.
          </Text>
          <Group gap="xs">
            <Button size="xs" variant="default" loading={trocando} onClick={() => { void trocar() }}>
              Trocar para {alvo.ambiente}
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setEscolhido(null)}>Manter {atual}</Button>
          </Group>
        </Stack>
      )}
      {alvo && alvo.estado !== 'disponivel' && (
        <Alert color="yellow" variant="light" role="alert"
               icon={<IconAlertTriangle size={16} stroke={1.5} />}
               title={`O rascunho não vai para o ${alvo.ambiente} agora`}>
          <Text size="sm" style={BARLOW}>{alvo.caminho}</Text>
        </Alert>
      )}
      {recusa && (
        <Alert color="red" variant="light" role="alert">
          <Text size="sm" style={BARLOW}>{recusa}</Text>
        </Alert>
      )}
    </Stack>
  )
}
